"use client";
import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../services/api';
import { Trash2, Plus, Edit } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ModalExclusao = ({ onConfirm, onCancel, loading }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4" aria-modal="true" role="dialog">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 animate-fade-in-scale">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Confirmar Exclusão</h2>
            <p className="text-slate-600">
                Tem a certeza que deseja excluir esta formação? Esta ação não poderá ser desfeita.
            </p>
            <div className="mt-6 flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold"
                    disabled={loading}
                >
                    Cancelar
                </button>
                <button
                    onClick={onConfirm}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                    disabled={loading}
                >
                    {loading ? 'Excluindo...' : 'Excluir'}
                </button>
            </div>
        </div>
    </div>
);



const ModalFormacao = ({ isOpen, onClose, onSave, formacao, grupo, loading }) => {
    const [dadosFormulario, setDadosFormulario] = useState({
        titulo: '',
        links: '',
        descricao: '',
    });
    const [formError, setFormError] = useState('');
    const [apiError, setApiError] = useState('');
    const tituloRef = useRef(null);

    useEffect(() => {
        if (formacao) {
            setDadosFormulario({
                titulo: formacao.titulo || '',
                links: formacao.links || '',
                descricao: formacao.descricao || '',
            });
        } else {
            setDadosFormulario({ titulo: '', links: '', descricao: '' });
        }
        setFormError('');
    }, [formacao, isOpen]);

    useEffect(() => {
        if (isOpen && tituloRef.current) {
            tituloRef.current.focus();
        }
    }, [isOpen]);

    const validateURL = (url) => {
        if (!url) return true;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const handleChange = (e) => {
        setDadosFormulario({ ...dadosFormulario, [e.target.name]: e.target.value });
        if (e.target.name === 'titulo') {
            if (!e.target.value.trim()) {
                setFormError('O título não pode estar vazio.');
            } else {
                setFormError('');
            }
        }
        if (e.target.name === 'links') {
            if (e.target.value && !validateURL(e.target.value)) {
                setFormError('O link deve ser uma URL válida.');
            } else {
                setFormError('');
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setApiError('');
        if (!grupo) {
            setFormError('Erro: Grupo não encontrado.');
            return;
        }
        if (!dadosFormulario.titulo.trim()) {
            setFormError('O título não pode estar vazio.');
            return;
        }
        if (dadosFormulario.links && !validateURL(dadosFormulario.links)) {
            setFormError('O link deve ser uma URL válida.');
            return;
        }
        const payload = {
            ...dadosFormulario,
            paroquia_id: grupo.paroquia_id,
            comunidade_id: grupo.comunidade_id,
            grupo_id: grupo.grupo_id,
        };
        onSave(payload, formacao?.id, setApiError);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40 p-4" aria-modal="true" role="dialog">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md animate-fade-in-scale">
                <h2 className="text-xl font-bold mb-4">
                    {formacao ? 'Editar Formação' : 'Nova Formação'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4" aria-label="Formulário de formação">
                    <input
                        type="text"
                        name="titulo"
                        placeholder="Título"
                        value={dadosFormulario.titulo}
                        onChange={handleChange}
                        className="w-full p-3 border border-slate-300 rounded-lg"
                        required
                        ref={tituloRef}
                        disabled={loading}
                        aria-label="Título da formação"
                    />
                    <input
                        type="url"
                        name="links"
                        placeholder="Link do vídeo (opcional)"
                        value={dadosFormulario.links}
                        onChange={handleChange}
                        className="w-full p-3 border border-slate-300 rounded-lg"
                        disabled={loading}
                        aria-label="Link do vídeo"
                    />
                    <textarea
                        name="descricao"
                        placeholder="Descrição"
                        value={dadosFormulario.descricao}
                        onChange={handleChange}
                        className="w-full p-3 border border-slate-300 rounded-lg"
                        rows="4"
                        disabled={loading}
                        aria-label="Descrição da formação"
                    ></textarea>
                    {formError && <div className="text-red-500 mt-2" aria-live="assertive">{formError}</div>}
                    {apiError && <div className="text-red-500 mt-2" aria-live="assertive">{apiError}</div>}
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={() => { setFormError(''); setApiError(''); onClose(); }} className="px-5 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold" disabled={loading} aria-label="Cancelar">
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-5 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700"
                            disabled={!!formError || loading}
                            aria-label="Salvar formação"
                        >
                            {loading ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente Principal ---
export default function GerenciadorFormacoes({ grupo, usuarioPainel, token }) {
    const [formacoes, setFormacoes] = useState([]);
    const [formacaoSelecionada, setFormacaoSelecionada] = useState(null);
    const [modalFormAberto, setModalFormAberto] = useState(false);
    const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
    const [idParaExcluir, setIdParaExcluir] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [loadingSalvar, setLoadingSalvar] = useState(false);
    const [loadingExcluir, setLoadingExcluir] = useState(false);

    const carregarDados = useCallback(async (isBackground = false) => {
        if (!token || !grupo?.grupo_id) return;
        try {
            if (!isBackground) setLoading(true);
            setError('');
            const response = await api.get(`/formacoes/grupo/${grupo.grupo_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormacoes(response.data || []);
        } catch (err) {
            setError('Erro ao carregar formações do grupo.');
            setFormacoes([]);
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, [token, grupo]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    const abrirModalEdicao = (formacao) => {
        setFormacaoSelecionada(formacao);
        setModalFormAberto(true);
        setError('');
    };

    const abrirModalCriacao = () => {
        setFormacaoSelecionada(null);
        setModalFormAberto(true);
        setError('');
    };

    const fecharModalForm = () => {
        setModalFormAberto(false);
        setFormacaoSelecionada(null);
        setError('');
    };

    const salvarFormacao = async (dados, id, setApiError) => {
        setLoadingSalvar(true);
        try {
            let savedFormacao;
            if (id) {
                const res = await api.put(`/formacoes-admin/${id}`, dados, { headers: { Authorization: `Bearer ${token}` } });
                savedFormacao = res.data;
                setFormacoes(prev => prev.map(f => f.id === id ? { ...f, ...savedFormacao } : f));
            } else {
                const res = await api.post('/formacoes-admin', dados, { headers: { Authorization: `Bearer ${token}` } });
                savedFormacao = res.data;
                setFormacoes(prev => [savedFormacao, ...prev]);
            }
            fecharModalForm();
            toast.success('Formação salva com sucesso!');
            carregarDados(true);
        } catch (err) {
            let msg = 'Erro ao salvar formação.';
            if (err?.response?.data?.message) {
                msg = err.response.data.message;
            }
            setError(msg);
            if (setApiError) setApiError(msg);
            toast.error(msg);
        } finally {
            setLoadingSalvar(false);
        }
    };

    const confirmarExclusao = (id) => {
        setIdParaExcluir(id);
        setModalExcluirAberto(true);
        setError('');
    };

    const excluirConfirmado = async () => {
        setLoadingExcluir(true);
        try {
            await api.delete(`/formacoes-admin/${idParaExcluir}`, { headers: { Authorization: `Bearer ${token}` } });
            setFormacoes(prev => prev.filter(f => f.id !== idParaExcluir));
            setModalExcluirAberto(false);
            setIdParaExcluir(null);
            toast.success('Formação excluída com sucesso!');
            carregarDados(true);
        } catch (err) {
            setError('Erro ao excluir formação.');
            toast.error('Erro ao excluir formação.');
        } finally {
            setLoadingExcluir(false);
        }
    };

    // Permissão: só coordenador OU admin pode acessar
    const roles = (usuarioPainel?.roles || []).map(r => r.toLowerCase());
    if (!roles.includes('coordenador') && !roles.includes('admin')) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
                <div className="bg-white p-8 rounded-xl shadow text-center max-w-md">
                    <h2 className="text-2xl font-bold text-red-600 mb-2">Acesso negado</h2>
                    <p className="text-gray-700 mb-6">Você não tem permissão para acessar este painel.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 font-sans">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <style>{`.animate-fade-in-scale { animation: fadeInScale 0.3s ease-out forwards; } @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
                <h1 className="text-3xl font-bold text-slate-800">Formações</h1>
                {grupo && (
                    <button
                        onClick={abrirModalCriacao}
                        className="mt-4 sm:mt-0 bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        disabled={loadingSalvar || loadingExcluir}
                    >
                        <Plus size={20} />
                        {loadingSalvar ? 'Salvando...' : loadingExcluir ? 'Excluindo...' : 'Adicionar Formação'}
                    </button>
                )}
            </div>

            {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-4 text-center font-semibold">
                    {error}
                </div>
            )}

            {loading ? <p className="text-center text-slate-500">A carregar...</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {formacoes.map((formacao) => (
                        <div key={formacao.id} className="bg-white p-6 rounded-xl shadow-md border border-slate-200 flex flex-col justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">{formacao.titulo}</h2>
                                <p className="text-sm text-slate-600 mt-2">{formacao.descricao}</p>
                                {formacao.links && (
                                    <a
                                        href={formacao.links}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 text-sm font-semibold underline block mt-4"
                                    >
                                        Assistir Vídeo
                                    </a>
                                )}
                            </div>
                            <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end gap-2">
                                <button
                                    onClick={() => abrirModalEdicao(formacao)}
                                    className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                                    disabled={loadingSalvar || loadingExcluir}
                                >
                                    <Edit size={18} />
                                </button>
                                <button
                                    onClick={() => confirmarExclusao(formacao.id)}
                                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                                    disabled={loadingSalvar || loadingExcluir}
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
             {formacoes.length === 0 && !loading && (
                <div className="text-center py-16 px-6 bg-slate-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-slate-700">Nenhuma formação encontrada</h3>
                    <p className="text-slate-500 mt-2">Clique em "Adicionar Formação" para começar.</p>
                </div>
            )}

            <ModalFormacao 
                isOpen={modalFormAberto} 
                onClose={fecharModalForm}
                onSave={salvarFormacao}
                formacao={formacaoSelecionada}
                grupo={grupo}
                loading={loadingSalvar}
            />

            {modalExcluirAberto && (
                <ModalExclusao 
                    onConfirm={excluirConfirmado}
                    onCancel={() => setModalExcluirAberto(false)}
                    loading={loadingExcluir}
                />
            )}
        </div>
    );
}
